import { Bugsnag } from '@bugsnag/browser';

import {
  sendAnalyticsEvent,
  sendBingEvent,
  sendGAEvent
} from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_USER_JOURNEYS,
  BING_CONVERSION_EVENT_ACTIONS,
  EVENT_ACTION_IDS
} from 'dpl/constants/analytics';
import { PAYMENT_REQUEST_CATEGORIES } from 'dpl/shared/constants/breeder_app';
import { removeEmptyValues } from 'dpl/shared/utils/object';

// This will not work if we go german as they flip the '.' and the ',' notation
export function formatUSCurrency(currency) {
  if (currency == null) {
    return 0;
  }
  // if it's already a number no need to convert
  if (typeof currency === 'number') {
    return currency;
  }

  return Number(currency.replace(/[^0-9.-]+/g, ''));
}

export function retrievePaymentInitatedBy(paymentRequest) {
  if (
    !paymentRequest.initiated_by_breeder &&
    !paymentRequest.initiated_by_buyer
  ) {
    return null;
  }

  return paymentRequest.initiated_by_breeder ? 'breeder' : 'buyer';
}

export function createPaymentFeeProperties(paymentFeeData) {
  // return an empty object so we can still perform a spread safely
  if (!paymentFeeData) {
    return {};
  }

  const paymentFeeProperties = {
    payment_amount_total:
      formatUSCurrency(paymentFeeData.total_in_dollars) || null,
    payment_good_dog_fee:
      formatUSCurrency(paymentFeeData.fee_in_dollars) || null
  };

  return removeEmptyValues(paymentFeeProperties);
}

export function createPaymentProperties(paymentRequest, paymentFeeData) {
  try {
    const paymentAmount = formatUSCurrency(
      paymentRequest.formatted_amount ?? paymentRequest.amount
    );
    const initiatedBy = retrievePaymentInitatedBy(paymentRequest);
    const paymentFeeProperties = createPaymentFeeProperties(paymentFeeData);

    const paymentProperties = {
      payment_amount: paymentAmount,
      payment_status: paymentRequest.status,
      payment_request_id: paymentRequest.id,
      buyer_profile_id:
        paymentRequest.buyer_profile?.id || paymentRequest.buyer_profile_id,
      payment_external_id: paymentRequest.external_id,
      payment_category: paymentRequest.category,
      payment_initiated_by: initiatedBy || paymentRequest.initiated_by,
      payment_method: paymentRequest.payment_method,
      breeder_profile_id:
        paymentRequest.breeder_profile?.id || paymentRequest.breeder_profile_id,
      ...paymentFeeProperties
    };

    return removeEmptyValues(paymentProperties);
  } catch (e) {
    Bugsnag.notify(e);
    return {};
  }
}

/**
 *
 * @param paymentRequestInfo an object that allows you to pass in the following
 * - paymentRequest(required)
 * - paymentFee
 * - buttonCta (required)
 * - userJourney (defaults to ANALYTICS_USER_JOURNEYS.PAYMENTS)
 * - view
 * - viewFeature
 * - viewFeatureLocation
 */
export function trackPaymentEvent(paymentRequestInfo) {
  try {
    const paymentProperties = createPaymentProperties(
      paymentRequestInfo.paymentRequest,
      paymentRequestInfo.paymentFee
    );

    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      user_journey:
        paymentRequestInfo.userJourney ?? ANALYTICS_USER_JOURNEYS.PAYMENTS,
      view: paymentRequestInfo.view,
      view_feature: paymentRequestInfo.viewFeature,
      view_feature_location: paymentRequestInfo.viewFeatureLocation,
      button_cta: paymentRequestInfo.buttonCta,
      payment_request_id: paymentRequestInfo.paymentRequestId,
      ...paymentProperties
    });
  } catch (e) {
    Bugsnag.notify(e);
  }
}

export function trackPaymentConversion(category, amountInDollars) {
  let eventId;
  let bingEvent;

  if (category === PAYMENT_REQUEST_CATEGORIES.DEPOSIT) {
    eventId = EVENT_ACTION_IDS.DEPOSIT_PAYMENT;
    bingEvent = BING_CONVERSION_EVENT_ACTIONS.DEPOSIT_PAYMENT;
  } else if (category === PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT) {
    eventId = EVENT_ACTION_IDS.DOG_PAYMENT;
    bingEvent = BING_CONVERSION_EVENT_ACTIONS.FINAL_PAYMENT;
  } else {
    eventId = EVENT_ACTION_IDS.OTHER_PAYMENT;
  }

  if (bingEvent) {
    sendBingEvent(bingEvent, {
      revenue_value: amountInDollars,
      currency: 'USD'
    });
  }

  return sendGAEvent('conversion', {
    send_to: eventId,
    value: amountInDollars
  });
}
